import type { ComponentProps, MouseEvent } from "react";
import { navigate } from "../../navigation/history";

interface AppLinkProps extends Omit<ComponentProps<"a">, "href"> {
    /** The address the link names — what a new tab, a bookmark or a copy of the link gets. */
    to: string;
    /**
     * Runs instead of moving the app on a plain click, for a caller that has
     * to decide first — the rail, which checks the page is open to this user.
     */
    onNavigate?: () => void;
    /** The link names the page on screen. */
    active?: boolean;
}

/**
 * A link into the app that is a real link.
 *
 * The rail used to be buttons, and a button has no address: middle-click, ⌘/Ctrl-click,
 * "open in new tab" and "copy link" did nothing, because there was nothing for the
 * browser to open. An `<a href>` gives it one; a plain primary click is still handled
 * in place, without a reload, and every other click is left to the browser.
 */
export function AppLink({ to, onNavigate, active = false, onClick, target, ...props }: AppLinkProps) {
    const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
        onClick?.(event);
        if (event.defaultPrevented) return;
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        if (target !== undefined && target !== "_self") return;
        event.preventDefault();
        if (onNavigate) {
            onNavigate();
        } else {
            navigate(to);
        }
    };

    return (
        <a
            {...props}
            href={to}
            target={target}
            aria-current={active ? "page" : props["aria-current"]}
            onClick={handleClick}
        />
    );
}
